import { throttle } from 'lodash-es';
import { useCallback, useEffect, useRef, useState } from 'react';
import type { MutableRefObject } from 'react';

type UseStickyPositionOptions = {
  fixedTopPosition: number;
  initialTopPosition: number;
  maxHeight: number;
  stickyThreshold: number;
};

type UseStickyPositionResult = {
  isFixed: boolean;
  stickyElementRef: MutableRefObject<HTMLDivElement | null>;
  topPosition: number;
  wrapperRef: MutableRefObject<HTMLDivElement | null>;
};

export const useStickyPosition = ({
  fixedTopPosition,
  initialTopPosition,
  maxHeight,
  stickyThreshold,
}: UseStickyPositionOptions): UseStickyPositionResult => {
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const stickyElementRef = useRef<HTMLDivElement | null>(null);
  const [stickyPosition, setStickyPosition] = useState({
    isFixed: false,
    topPosition: initialTopPosition,
  });

  const updateStickyPosition = useCallback((): void => {
    const scrollPosition = Math.abs(document.body.getBoundingClientRect().top);

    if (scrollPosition < stickyThreshold) {
      setStickyPosition({
        isFixed: false,
        topPosition: initialTopPosition,
      });
      return;
    }

    const footer = document.querySelector('footer');
    const wrapper = wrapperRef.current;
    const stickyElement = stickyElementRef.current;

    if (footer && wrapper && stickyElement) {
      const stickyElementHeight =
        stickyElement.getBoundingClientRect().height ||
        stickyElement.offsetHeight ||
        maxHeight;
      const footerTop = footer.getBoundingClientRect().top;

      if (footerTop <= fixedTopPosition + stickyElementHeight) {
        const wrapperTop = wrapper.getBoundingClientRect().top;

        setStickyPosition({
          isFixed: false,
          topPosition: Math.round(footerTop - stickyElementHeight - wrapperTop),
        });
        return;
      }
    }

    setStickyPosition({
      isFixed: true,
      topPosition: initialTopPosition,
    });
  }, [fixedTopPosition, initialTopPosition, maxHeight, stickyThreshold]);

  useEffect(() => {
    const stickyCbThrottled = throttle(updateStickyPosition, 100);
    window.addEventListener('scroll', stickyCbThrottled);

    return () => {
      window.removeEventListener('scroll', stickyCbThrottled);
      stickyCbThrottled.cancel();
    };
  }, [updateStickyPosition]);

  return {
    isFixed: stickyPosition.isFixed,
    stickyElementRef,
    topPosition: stickyPosition.topPosition,
    wrapperRef,
  };
};
